import React, { useEffect, useState, useCallback } from 'react';
import { X, Smartphone, Share, Plus } from 'lucide-react';

const DISMISS_KEY = 'ckm_pwa_banner_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // re-show after 7 days

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  const shouldShow = useCallback(() => {
    if (typeof window === 'undefined') return false;
    if (isStandalone()) return false; // already installed
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return false;
    return true;
  }, []);

  useEffect(() => {
    if (!shouldShow()) return;

    // Chromium / Android — capture native install prompt
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    // iOS Safari — no native prompt; show manual instructions
    if (isIOS()) {
      setVisible(true);
    }

    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [shouldShow]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setVisible(false);
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      setShowIOSHelp(true);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!visible) return null;

  return (
    <>
      {/* Sticky banner — top of viewport, mobile-first */}
      <div
        className="sticky top-0 z-50 bg-gradient-to-r from-[#C45D35] to-[#8B4513] text-white shadow-lg"
        data-testid="pwa-install-banner"
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="hidden sm:flex h-10 w-10 rounded-full bg-white/15 items-center justify-center flex-shrink-0">
              <Smartphone className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-sm sm:text-base font-semibold leading-tight truncate">
                📱 Install the Marketplace App
              </p>
              <p className="text-xs sm:text-sm text-white/85 leading-tight truncate">
                Faster shopping, offline browsing, instant order alerts
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleInstallClick}
              className="bg-white text-[#8B4513] font-semibold text-sm px-4 py-2 rounded-full hover:bg-[#FFF3E8] transition-colors whitespace-nowrap"
              data-testid="pwa-install-button"
            >
              Install
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss install banner"
              className="text-white/80 hover:text-white p-1"
              data-testid="pwa-install-dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* iOS instructions modal */}
      {showIOSHelp && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSHelp(false)}
          data-testid="pwa-ios-help-overlay"
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold text-[#8B4513]">
                Add to Home Screen
              </h3>
              <button
                type="button"
                onClick={() => setShowIOSHelp(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#C45D35] text-white text-xs font-bold flex items-center justify-center">
                  1
                </span>
                <span>
                  Tap the <strong>Share</strong> button{' '}
                  <Share className="inline h-4 w-4 align-text-bottom text-[#C45D35]" />{' '}
                  at the bottom of Safari
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#C45D35] text-white text-xs font-bold flex items-center justify-center">
                  2
                </span>
                <span>
                  Scroll and tap{' '}
                  <strong>
                    Add to Home Screen{' '}
                    <Plus className="inline h-4 w-4 align-text-bottom text-[#C45D35]" />
                  </strong>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#C45D35] text-white text-xs font-bold flex items-center justify-center">
                  3
                </span>
                <span>
                  Tap <strong>Add</strong> in the top-right corner
                </span>
              </li>
            </ol>
            <button
              type="button"
              onClick={() => setShowIOSHelp(false)}
              className="mt-6 w-full bg-[#C45D35] text-white font-semibold py-2.5 rounded-full hover:bg-[#A04A28] transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
